import React, { useEffect } from "react";
import "./customer-invoice-details.scss";
import { useMatch, useNavigate, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearNotifications,
  downloadCustomerInvoice,
  getCustomerInvoiceDetails,
  selectInvoiceDetails,
  selectPaymentDetails,
} from "../../reducers/invoiceSlice";
import {
  GSEmptyList,
  GSItemList,
  GSListPage,
  GSPageSection,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
} from "golfstatus_react_components";
import {
  faCircleArrowDown,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import RegistrationOrderSummary from "../registration-order-summary";
import CustomerInvoiceSummary from "./customer-invoice-summary";
import CustomerInvoiceTotalBanner from "./customer-invoice-total-banner";
import CustomerInvoiceContactInformation from "./customer-invoice-contact-information";
import CustomerInvoiceBillingInformation from "./customer-invoice-billing-information";
import { getNotificationItemInfo } from "../../helpers/Converters";

const InvoiceDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const match = useMatch("/invoice/:id/*");

  const details = useSelector(selectInvoiceDetails);
  const paymentDetails = useSelector(selectPaymentDetails);

  useEffect(() => {
    if (match?.params?.id) {
      dispatch(getCustomerInvoiceDetails(match.params.id));
    }
  }, [dispatch, match?.params?.id]);

  const payInvoice = () => {
    dispatch(clearNotifications());
    navigate(`pay`);
  };

  const downloadInvoice = () => {
    dispatch(downloadCustomerInvoice(details?.id));
  };

  const getPageActions = () => {
    if (!details) {
      return [];
    }
    let actions = [];
    if (isInvoicePayable()) {
      actions.push({
        id: 1,
        buttonTitle: "Pay Invoice",
        type: "black",
        actionClick: payInvoice,
      });
    }
    actions.push({
      id: 2,
      buttonTitle: "Download Invoice",
      type: "grey mobile-icon",
      actionIcon: faCircleArrowDown,
      actionClick: downloadInvoice,
    });
    return actions;
  };

  const isInvoicePaid = () => {
    return details?.registrationOrder?.status === "paid";
  };

  const isInvoiceVoid = () => {
    return details?.registrationOrder?.status === "void";
  };

  const isInvoicePending = () => {
    return details?.registrationOrder?.status === "pending";
  };

  const isInvoicePayable = () => {
    return !isInvoicePaid() && !isInvoiceVoid() && !isInvoicePending();
  };

  const getInvoiceInfo = () => {
    const context = {
      details: details,
    };

    return (
      <GSPageSection
        body={[
          <CustomerInvoiceSummary
            style={{ ...defaultLayouts.fullWidth }}
            {...context}
          />,
        ]}
      />
    );
  };

  const getBillingInfo = () => {
    const context = {
      details: details,
    };

    return (
      <GSPageSection
        body={[<CustomerInvoiceBillingInformation {...context} />]}
      />
    );
  };

  const getOrderSummary = () => {
    const context = {
      details: details,
      invoiceDetailsView: true,
      registrantCoversFees: () => {
        return (
          details?.tournament?.platformFeeEnabled &&
          !details?.tournament?.organizationPaysPlatformFee
        );
      },
      registrantCanOptToCoverFees: () => {
        return (
          details?.tournament?.organizationPaysPlatformFee &&
          details?.tournament?.allowRegistrantCoverFees
        );
      },
      isRegistrantCoveringFees: () => {
        return (
          context.registrantCoversFees() ||
          (context.registrantCanOptToCoverFees() &&
            paymentDetails?.registrantCoversFees)
        );
      },
    };

    return <GSPageSection body={[<RegistrationOrderSummary {...context} />]} />;
  };

  const getInvoiceTotal = () => {
    const context = {
      details: details,
      payInvoice: payInvoice,
    };

    return (
      <GSPageSection body={[<CustomerInvoiceTotalBanner {...context} />]} />
    );
  };

  const getContactInfo = () => {
    const context = {
      details: details,
    };

    return (
      <GSPageSection
        body={[<CustomerInvoiceContactInformation {...context} />]}
      />
    );
  };

  const getInvoiceDetailItems = () => {
    return details
      ? [
          getInvoiceInfo(),
          getBillingInfo(),
          getOrderSummary(),
          getInvoiceTotal(),
          getContactInfo(),
        ]
      : [];
  };

  const getNoInvoiceMessage = () => {
    return (
      <GSEmptyList
        title="We couldn't find the invoice you're looking for..."
        detail="Reach out to the tournament organizer with any questions regarding invoices."
      />
    );
  };

  const getDefaultBanner = () => {
    if (isInvoicePending()) {
      return {
        title: getNotificationItemInfo(
          `It can take up to 7 business days for bank account transactions to clear.`,
          faSpinner,
          `Payment Pending`
        ),
        state: "grey",
      };
    }
  };

  const getInvoiceDetails = () => {
    return (
      <GSItemList
        items={getInvoiceDetailItems()}
        type="vertical"
        emptyMessage={getNoInvoiceMessage()}
        listItem={(item) => item}
      ></GSItemList>
    );
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const isSidePanelOpen = () => {
    return match && match?.params?.["*"] !== "";
  };

  const getDetailsPage = () => {
    return {
      title: <div style={{ ...defaultTypography.headline1 }}>Invoice Details</div>,
      headerStyle: {...defaultPadding.xLargePad},
      getItemList: getInvoiceDetails,
      getPageActions: getPageActions,
      getRouter: getRouter,
      isSidePanelOpen: isSidePanelOpen,
    };
  };

  return (
    <customer-invoice-details>
      <GSListPage
        getBanner={isInvoicePending() ? getDefaultBanner : false}
        {...getDetailsPage()}
      />
    </customer-invoice-details>
  );
};

export default InvoiceDetails;
